import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import ProfessionalLayout from '../../components/layout/ProfessionalLayout';
import { employeeAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, Copy, Check, ExternalLink, Download, QrCode, Eye, Link2, UserCheck, UserX, Trash2, FileText, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import ScoreExplainerModal from '../../components/common/ScoreExplainerModal';
import './VettIdPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VettIdPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [tierData, setTierData] = useState(null);
  const [showScoreExplainer, setShowScoreExplainer] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [downloadingPassport, setDownloadingPassport] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageTab, setManageTab] = useState('requests'); // 'requests', 'grants', 'links'
  const [accessRequests, setAccessRequests] = useState([]);
  const [accessGrants, setAccessGrants] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [shareAnalytics, setShareAnalytics] = useState({ total_links: 0, total_views: 0, active_links: 0 });
  const [loadingManage, setLoadingManage] = useState(false);
  const [downloadingPortfolio, setDownloadingPortfolio] = useState(false);
  const cardRef = useRef(null);
  const qrRef = useRef(null);

  // Score breakdown components with colors
  const scoreComponents = [
    { key: 'trust', label: 'Trust', color: '#4F8EF7', description: 'Consistency between what you reported and what employers co-signed, across every record.' },
    { key: 'performance', label: 'Performance', color: '#00C9A7', description: 'Weighted average of manager ratings across your published, rated records.' },
    { key: 'consistency', label: 'Consistency', color: '#F0B429', description: 'Tenure length and gap patterns across your career history. Improves as more roles are added.' },
    { key: 'verification_depth', label: 'Verification Depth', color: '#FF9F6B', description: 'How many fields per record were corroborated by your employer versus self-reported.' },
    { key: 'tenure', label: 'Tenure', color: '#4F8EF7', description: 'Career length and role progression across your published record history.' },
    { key: 'employer_standing', label: 'Employer Standing', color: '#00C9A7', description: 'Average tier of the companies that have co-signed your records.' },
    { key: 'record_recency', label: 'Record Recency', color: '#F0B429', description: 'How current your most recently verified record is.' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // 3D tilt effect for card
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / 10;
      const rotateY = (centerX - x) / 10;
      
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    };

    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [loading]);

  const fetchData = async () => {
    try {
      const [profileRes, tierRes] = await Promise.all([
        employeeAPI.getProfile(),
        employeeAPI.getVettTier().catch(() => ({ data: null }))
      ]);
      setProfile(profileRes.data);
      setTierData(tierRes.data);
    } catch (error) {
      console.error('Error fetching VETT ID data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadQRCode = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    // Create canvas from SVG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = '#F5F8FF';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 50, 50, 300, 300);
      
      // Add VETT branding
      ctx.fillStyle = '#00C9A7';
      ctx.font = 'bold 16px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('VETT ID', 200, 30);
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '14px monospace';
      ctx.fillText(vettId, 200, 380);
      
      // Download
      const link = document.createElement('a');
      link.download = `vett-qr-${vettId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('QR code downloaded!');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
  };

  const downloadPassport = async () => {
    setDownloadingPassport(true);
    try {
      const response = await fetch(`${API_URL}/api/passport/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download passport');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vett-passport-${vettId}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Passport downloaded! Open the file and print to PDF.');
    } catch (error) {
      console.error('Error downloading passport:', error);
      toast.error('Failed to download passport');
    } finally {
      setDownloadingPassport(false);
    }
  };

  const downloadPortfolio = async () => {
    setDownloadingPortfolio(true);
    try {
      const response = await fetch(`${API_URL}/api/passport/portfolio`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download portfolio');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vett-portfolio-${vettId}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Portfolio downloaded! Open the file and print to PDF.');
    } catch (error) {
      console.error('Error downloading portfolio:', error);
      toast.error('Failed to download portfolio');
    } finally {
      setDownloadingPortfolio(false);
    }
  };

  const fetchManageData = async () => {
    setLoadingManage(true);
    try {
      const [requestsRes, grantsRes, linksRes] = await Promise.all([
        fetch(`${API_URL}/api/sharing/access-requests/incoming`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/sharing/access-grants`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/sharing/records/my-links`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setAccessRequests(data.access_requests || []);
      }
      if (grantsRes.ok) {
        const data = await grantsRes.json();
        setAccessGrants(data.grants || []);
      }
      if (linksRes.ok) {
        const data = await linksRes.json();
        setShareLinks(data.share_links || []);
        setShareAnalytics(data.analytics || { total_links: 0, total_views: 0, active_links: 0 });
      }
    } catch (error) {
      console.error('Error fetching manage data:', error);
    } finally {
      setLoadingManage(false);
    }
  };

  const handleAccessResponse = async (requestId, approved) => {
    try {
      const response = await fetch(`${API_URL}/api/sharing/access-requests/${requestId}/respond?approved=${approved}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to respond');
      
      toast.success(approved ? 'Access granted!' : 'Request declined');
      fetchManageData();
    } catch (error) {
      toast.error('Failed to respond to request');
    }
  };

  const handleRevokeAccess = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/sharing/access-grants/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to revoke');
      
      toast.success('Access revoked');
      fetchManageData();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const handleRevokeShareLink = async (linkToken) => {
    try {
      const response = await fetch(`${API_URL}/api/sharing/records/${linkToken}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to revoke');
      
      toast.success('Share link revoked');
      fetchManageData();
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  const openManageModal = () => {
    setShowManageModal(true);
    fetchManageData();
  };

  const getTierClass = (tier) => {
    const classes = {
      'NONE': 'tier-unverified',
      'BASIC': 'tier-unverified',
      'VERIFIED': 'tier-verified',
      'ESTABLISHED': 'tier-established',
      'DISTINGUISHED': 'tier-distinguished'
    };
    return classes[tier] || 'tier-unverified';
  };

  const vettId = profile?.vett_id || `VP-${user?.id?.slice(0, 6).toUpperCase() || 'XXXXX'}`;
  const lookupLink = `vettpro.org/id/${vettId}`;

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F8EF7]" />
        </div>
      </ProfessionalLayout>
    );
  }

  // Generate mock score breakdown if not available
  const scoreBreakdown = tierData?.score_breakdown || {
    trust: 78,
    performance: 81,
    consistency: 63,
    verification_depth: 60,
    tenure: 74,
    employer_standing: 84,
    record_recency: 82
  };

  return (
    <ProfessionalLayout>
      {/* Dark Section - VETT ID Card + Score Breakdown */}
      <section className="vett-id-dark-section">
        <div className="vett-id-dark-bg" />
        <div className="vett-id-dark-inner">
          
          {/* Header */}
          <div className="vett-id-header">
            <div className="vett-id-eyebrow">YOUR VETT ID</div>
            <h1 className="vett-id-title">A permanent professional identity.</h1>
            <p className="vett-id-subtitle">Shareable, verifiable, and issued once for life. Move your mouse over the card.</p>
            <button 
              className="vett-id-download-btn"
              onClick={downloadPassport}
              disabled={downloadingPassport}
              data-testid="download-passport-btn"
            >
              {downloadingPassport ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
              ) : (
                <><Download className="w-4 h-4" /> Download Passport</>
              )}
            </button>
          </div>

          {/* 3D Card */}
          <div className="vett-card-3d-container">
            <div className="vett-card-3d" ref={cardRef} data-testid="vett-id-card">
              <div className="card3d-logo">VETT PRO</div>
              <div className="card3d-chip" />
              <div className="card3d-id">{vettId}</div>
              <div className="card3d-name">{profile?.name || user?.name || 'Professional'}</div>
              <div className="card3d-bottom">
                <div>
                  <div className="card3d-score-label">VETT SCORE</div>
                  <div className="card3d-score-val">{tierData?.score || 0}</div>
                </div>
                <div className={`card3d-tier ${getTierClass(tierData?.tier)}`}>
                  {tierData?.tier_name || 'UNVERIFIED'}
                </div>
              </div>
            </div>
          </div>

          {/* Score Breakdown Grid */}
          <div className="vett-id-breakdown-grid" data-testid="score-breakdown-grid">
            {scoreComponents.map((component) => {
              const value = scoreBreakdown[component.key] || 0;
              return (
                <div key={component.key} className="breakdown-card">
                  <div className="breakdown-label">{component.label}</div>
                  <div className="breakdown-val-row">
                    <span className="breakdown-val">{value}</span>
                    <span className="breakdown-val-max">/ 100</span>
                  </div>
                  <div className="breakdown-bar-track">
                    <div 
                      className="breakdown-bar-fill" 
                      style={{ 
                        width: `${value}%`, 
                        background: component.color 
                      }} 
                    />
                  </div>
                  <div className="breakdown-desc">{component.description}</div>
                </div>
              );
            })}
          </div>

          {/* Stats Row */}
          <div className="vett-id-stats-row">
            <div>
              <div className="vett-id-stat-val">{tierData?.score || 0}</div>
              <div className="vett-id-stat-label">VETT SCORE</div>
            </div>
            <div>
              <div className="vett-id-stat-val">{tierData?.metrics?.verified_count || 0}</div>
              <div className="vett-id-stat-label">VERIFIED RECORDS</div>
            </div>
            <div>
              <div className="vett-id-stat-val">{tierData?.tier_name || 'UNVERIFIED'}</div>
              <div className="vett-id-stat-label">CURRENT TIER</div>
            </div>
            <div>
              <div className="vett-id-stat-val">{new Date(profile?.created_at || Date.now()).getFullYear()}</div>
              <div className="vett-id-stat-label">VETT ID SINCE</div>
            </div>
          </div>
        </div>
      </section>

      {/* Light Section - Tier Progression + Share */}
      <div className="vett-id-light-section">
        {/* Tier Progression */}
        <div className="pro-card" style={{ marginBottom: '24px' }}>
          <div className="pro-card-title" style={{ marginBottom: '16px' }}>Tier progression</div>
          <div className="tier-progression-row">
            <span className="pro-tier-pill pro-tier-unverified">UNVERIFIED</span>
            <span className="tier-arrow">→</span>
            <span className="pro-tier-pill pro-tier-verified">VERIFIED</span>
            <span className="tier-arrow">→</span>
            <span className={`pro-tier-pill ${tierData?.tier === 'ESTABLISHED' ? 'pro-tier-established current' : 'pro-tier-established'}`}>
              {tierData?.tier === 'ESTABLISHED' ? 'ESTABLISHED, YOU ARE HERE' : 'ESTABLISHED'}
            </span>
            <span className="tier-arrow">→</span>
            <span className={`pro-tier-pill ${tierData?.tier === 'DISTINGUISHED' ? 'pro-tier-distinguished current' : 'pro-tier-distinguished'}`}>
              DISTINGUISHED
            </span>
          </div>
          <p className="tier-hint">
            Established professionals typically have 2 to 3 published records with strong ratings. 
            Reaching Distinguished usually takes 4 or more published records with a high average rating and long tenure.
          </p>
        </div>

        {/* Share Your VETT ID */}
        <div className="pro-card">
          <div className="pro-card-title" style={{ marginBottom: '16px' }}>Share your VETT ID</div>
          <p className="pro-page-sub" style={{ marginBottom: '18px' }}>
            Recruiters and employers can look up your public summary using your VETT ID or lookup link. 
            Full record detail is only visible to employers you have explicitly granted access to.
          </p>
          
          <div className="share-row">
            <span className="share-label">VETT ID</span>
            <div className="share-value-row">
              <span className="share-value mono">{vettId}</span>
              <button 
                className="share-copy-btn"
                onClick={() => copyToClipboard(vettId, 'vettId')}
                data-testid="copy-vett-id"
              >
                {copiedField === 'vettId' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div className="share-row">
            <span className="share-label">Lookup link</span>
            <div className="share-value-row">
              <span className="share-value mono link">{lookupLink}</span>
              <button 
                className="share-copy-btn"
                onClick={() => copyToClipboard(`https://${lookupLink}`, 'link')}
                data-testid="copy-lookup-link"
              >
                {copiedField === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <a 
                href={`https://${lookupLink}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="share-external-btn"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
          
          {/* QR Code Section */}
          <div className="share-row" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="share-label">QR Code</span>
            <div className="share-value-row">
              <span className="share-value">Scan to view your public profile</span>
              <button 
                className="share-copy-btn"
                onClick={() => setShowQRModal(true)}
                data-testid="show-qr-btn"
                title="View QR Code"
              >
                <QrCode className="w-4 h-4" />
              </button>
              <button 
                className="share-copy-btn"
                onClick={downloadQRCode}
                data-testid="download-qr-btn"
                title="Download QR Code"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Hidden QR for download */}
          <div ref={qrRef} style={{ position: 'absolute', left: '-9999px' }}>
            <QRCodeSVG 
              value={`${API_URL}/id/${vettId}`}
              size={300}
              bgColor="#FFFFFF"
              fgColor="#F5F8FF"
              level="H"
              includeMargin={true}
            />
          </div>
        </div>

        {/* Manage Access & Sharing */}
        <div className="pro-card">
          <div className="pro-card-title" style={{ marginBottom: '16px' }}>Manage access & sharing</div>
          <p className="pro-page-sub" style={{ marginBottom: '18px' }}>
            Review access requests, manage who can view your profile, and track shared record links.
          </p>
          
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button 
              className="manage-btn"
              onClick={openManageModal}
              data-testid="manage-access-btn"
            >
              <UserCheck className="w-4 h-4" />
              Manage Access
            </button>
            <button 
              className="manage-btn"
              onClick={downloadPortfolio}
              disabled={downloadingPortfolio}
              data-testid="download-portfolio-btn"
            >
              {downloadingPortfolio ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              Download Portfolio
            </button>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="qr-modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="qr-modal" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
            <div className="qr-modal-header">
              <h3>Your VETT ID QR Code</h3>
              <button className="qr-modal-close" onClick={() => setShowQRModal(false)}>×</button>
            </div>
            <div className="qr-modal-body">
              <div className="qr-code-container">
                <QRCodeSVG 
                  value={`${API_URL}/id/${vettId}`}
                  size={200}
                  bgColor="#FFFFFF"
                  fgColor="#F5F8FF"
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="qr-modal-vettid mono">{vettId}</div>
              <p className="qr-modal-hint">
                Anyone who scans this code can view your public VETT profile
              </p>
            </div>
            <div className="qr-modal-actions">
              <button className="qr-download-btn" onClick={downloadQRCode}>
                <Download className="w-4 h-4" /> Download PNG
              </button>
              <button className="qr-copy-btn" onClick={() => copyToClipboard(`${API_URL}/id/${vettId}`, 'qrLink')}>
                {copiedField === 'qrLink' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Access Modal */}
      {showManageModal && (
        <div className="qr-modal-overlay" onClick={() => setShowManageModal(false)}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()} data-testid="manage-modal">
            <div className="manage-modal-header">
              <h3>Manage Access & Sharing</h3>
              <button className="qr-modal-close" onClick={() => setShowManageModal(false)}>×</button>
            </div>
            
            {/* Analytics Summary */}
            <div className="manage-analytics">
              <div className="analytics-stat">
                <BarChart3 className="w-4 h-4" />
                <span className="analytics-value">{shareAnalytics.total_views}</span>
                <span className="analytics-label">Total Views</span>
              </div>
              <div className="analytics-stat">
                <Link2 className="w-4 h-4" />
                <span className="analytics-value">{shareAnalytics.active_links}</span>
                <span className="analytics-label">Active Links</span>
              </div>
              <div className="analytics-stat">
                <UserCheck className="w-4 h-4" />
                <span className="analytics-value">{accessGrants.length}</span>
                <span className="analytics-label">Access Grants</span>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="manage-tabs">
              <button 
                className={`manage-tab ${manageTab === 'requests' ? 'active' : ''}`}
                onClick={() => setManageTab('requests')}
              >
                Requests ({accessRequests.filter(r => r.status === 'pending').length})
              </button>
              <button 
                className={`manage-tab ${manageTab === 'grants' ? 'active' : ''}`}
                onClick={() => setManageTab('grants')}
              >
                Grants ({accessGrants.length})
              </button>
              <button 
                className={`manage-tab ${manageTab === 'links' ? 'active' : ''}`}
                onClick={() => setManageTab('links')}
              >
                Share Links ({shareLinks.length})
              </button>
            </div>
            
            <div className="manage-content">
              {loadingManage ? (
                <div className="manage-loading">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Access Requests Tab */}
                  {manageTab === 'requests' && (
                    <div className="manage-list">
                      {accessRequests.filter(r => r.status === 'pending').length === 0 ? (
                        <div className="manage-empty">No pending access requests</div>
                      ) : (
                        accessRequests.filter(r => r.status === 'pending').map((request) => (
                          <div key={request.id} className="manage-item">
                            <div className="manage-item-info">
                              <div className="manage-item-name">{request.requester_name}</div>
                              <div className="manage-item-sub">{request.requester_company}</div>
                              <div className="manage-item-email">{request.requester_email}</div>
                              {request.message && (
                                <div className="manage-item-message">"{request.message}"</div>
                              )}
                            </div>
                            <div className="manage-item-actions">
                              <button 
                                className="manage-action-btn approve"
                                onClick={() => handleAccessResponse(request.id, true)}
                                title="Approve"
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                              <button 
                                className="manage-action-btn decline"
                                onClick={() => handleAccessResponse(request.id, false)}
                                title="Decline"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  
                  {/* Access Grants Tab */}
                  {manageTab === 'grants' && (
                    <div className="manage-list">
                      {accessGrants.length === 0 ? (
                        <div className="manage-empty">No access grants yet</div>
                      ) : (
                        accessGrants.map((grant, idx) => (
                          <div key={idx} className="manage-item">
                            <div className="manage-item-info">
                              <div className="manage-item-name">{grant.name}</div>
                              <div className="manage-item-sub">{grant.company}</div>
                              <div className="manage-item-email">{grant.email}</div>
                              {grant.granted_at && (
                                <div className="manage-item-date">
                                  Granted {new Date(grant.granted_at).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                            <div className="manage-item-actions">
                              <button 
                                className="manage-action-btn revoke"
                                onClick={() => handleRevokeAccess(grant.email)}
                                title="Revoke Access"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  
                  {/* Share Links Tab */}
                  {manageTab === 'links' && (
                    <div className="manage-list">
                      {shareLinks.length === 0 ? (
                        <div className="manage-empty">No share links created yet</div>
                      ) : (
                        shareLinks.map((link) => (
                          <div key={link.id} className={`manage-item ${link.is_expired ? 'expired' : ''}`}>
                            <div className="manage-item-info">
                              <div className="manage-item-name">{link.job_title}</div>
                              <div className="manage-item-sub">{link.employer_name}</div>
                              <div className="manage-item-stats">
                                <span><Eye className="w-3 h-3" /> {link.views} views</span>
                                <span className="mono">{link.token}</span>
                              </div>
                              <div className="manage-item-date">
                                {link.is_expired ? 'Expired' : `Expires ${new Date(link.expires_at).toLocaleDateString()}`}
                              </div>
                            </div>
                            <div className="manage-item-actions">
                              {!link.is_expired && (
                                <button 
                                  className="manage-action-btn copy"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${API_URL}/record/${link.token}`);
                                    toast.success('Link copied!');
                                  }}
                                  title="Copy Link"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              )}
                              <button 
                                className="manage-action-btn revoke"
                                onClick={() => handleRevokeShareLink(link.token)}
                                title="Revoke Link"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Score Explainer Modal */}
      <ScoreExplainerModal 
        isOpen={showScoreExplainer} 
        onClose={() => setShowScoreExplainer(false)} 
      />
    </ProfessionalLayout>
  );
};

export default VettIdPage;
